import classNames from 'classnames';
import { createSearchParams, Link, NavLink, useLocation } from 'react-router-dom';
import { ReactComponent as InfoIcon } from 'src/assets/info.svg';
import { ReactComponent as News } from 'src/assets/services/navbar/news.svg';
import { ReactComponent as LocationIcon } from 'src/assets/services/navbar/footter_locations.svg';
import * as r from 'src/constants/routes';
import { useGetCategoriesTags } from 'src/pages/services/api/queries';
import { MenuCategory, MenuItems, RoutItem, Tag } from 'src/types';
import css from './dropdown.module.scss';
import RequestEmailModal from 'src/components/request-modal/request-email-modal';
import { useMemo } from 'react';
import { useResize } from 'src/hooks/shared-hooks';

interface DropdownProps {
  onClose?: () => void;
  setCloseOnClick?: (close: boolean) => void;
}

export const MENU_SERVICES = r.SERVICES_ROUTES.map((i) => i.title);

export default function Dropdown({ onClose, setCloseOnClick }: Readonly<DropdownProps>) {
  const route = useLocation();
  const { data: categories = [] } = useGetCategoriesTags();
  const dimensions = useResize();
  const mobileMode = dimensions.width < 500;
  const staticRouts = [
    {
      title: r.NEWS.title,
      path: r.NEWS.path,
      logo: <News />
    },
    {
      title: r.KNOWLEDGE_BASE.title,
      path: r.KNOWLEDGE_BASE.path,
      logo: r.KNOWLEDGE_BASE.meta.menuIcon
    },
    {
      title: r.CONTACTS.title,
      path: r.CONTACTS.path,
      logo: r.CONTACTS.meta.menuIcon
    },
    {
      title: r.WORK_SEARCH.title,
      path: r.WORK_SEARCH.path,
      logo: r.WORK_SEARCH.meta.menuIcon
    },
    {
      title: r.BUSINESS_MAP.title,
      path: r.BUSINESS_MAP.path,
      logo: r.BUSINESS_MAP.meta.menuIcon
    },
    {
      title: r.EVENTS.title,
      path: r.EVENTS.path,
      logo: r.EVENTS.meta.menuIcon
    },
    {
      title: r.VETERAN_LOCATIONS.title,
      path: r.VETERAN_LOCATIONS.path,
      logo: <LocationIcon />
    }
  ];

  const dropdownItems = useMemo(() => {
    return mobileMode ? categories : categories.filter((c: MenuCategory) => MENU_SERVICES.includes(c.name));
  }, [categories, mobileMode]);

  const items = dropdownItems.map((c: MenuCategory) => {
    const path = r.SERVICES_ROUTES?.find((i: RoutItem) => i.title === c.name)?.path;
    return {
      title: c.name,
      path: path,
      tags: c.tags
    };
  });

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    onClose?.();
  };

  return (
    <div className={classNames(css.dropdown)}>
      <div className={css.container}>
        <div className={classNames(css.pages, css.menuCards)}>
          <div className={css.serviceCards}>
            {items.map((item: MenuItems) => (
              <div key={item.title} className={css.card} onClick={handleClose}>
                <NavLink to={item.path}>
                  {item.title} <span>&#10095;</span>
                </NavLink>
                <MenuCard item={item} />
              </div>
            ))}
          </div>
        </div>
        <div className={css.pages}>
          <RequestEmailModal
            onOpen={() => setCloseOnClick?.(false)}
            onClose={() => setCloseOnClick?.(true)}
            trigger={
              <div className={css.button}>
                <div className={css.icon}>
                  <InfoIcon />
                </div>
                <div className={css.btn_text}>
                  <p>Подати запит до ЦВС</p>
                  <span>&#10095;</span>
                </div>
              </div>
            }
          />
          <ul className={css.staticRoutes}>
            {staticRouts.map((item: MenuItems) => {
              return (
                <li /*NOSONAR*/
                  key={item.title}
                  className={route.pathname?.includes(item.path) ? css.active : ''}
                  onClick={handleClose}>
                  <NavLink to={item.path}>
                    <div className={classNames(css.icon, item.title === r.EVENTS.title ? css.event : '')}>
                      {item.logo}
                    </div>
                    {item.title}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function MenuCard({ item }: Readonly<{ item: MenuItems }>) {
  return (
    <ul className={css.tagsList}>
      {item.title === 'Кар’єрні матеріали' && (
        <li>
          <NavLink to={r.EDUCATION_COURSES.path}>Каталог курсів</NavLink>
        </li>
      )}
      {item.tags?.slice(0, 5).map((t: Tag) => {
        return (
          <li key={t.id}>
            <Link
              to={{
                pathname: item.path,
                search: createSearchParams({
                  tag: t.name
                }).toString()
              }}>
              {t.name}
            </Link>
          </li>
        );
      })}
      {item.tags && item.tags?.length > 5 && (
        <li className={css.moreTags}>
          <NavLink to={item.path}>
            <p> ще {item.tags?.length - 5}</p> <span>&#10095;</span>
          </NavLink>
        </li>
      )}
    </ul>
  );
}
